import { Badge, Details, EmptyState, type Tone } from "@emdur/ui";
import type { ReactNode } from "react";
import {
  permissionConsequence,
  permissionTitle,
  UNKNOWN_PERMISSION,
} from "../lib/permissionCopy";
import {
  type CapabilityItem,
  type ClientDiff,
  type Risk,
  riskCounts,
} from "../lib/clients";

export const RISK: Record<
  Risk | "unknown",
  { label: string; tone: Tone; strip: string }
> = {
  access: { label: "Grants access", tone: "danger", strip: "bg-danger" },
  money: { label: "Affects money", tone: "warning", strip: "bg-warning" },
  "personal-data": {
    label: "Uses personal data",
    tone: "accent",
    strip: "permission-personal-dot",
  },
  standard: { label: "Standard", tone: "neutral", strip: "bg-muted" },
  unknown: { label: UNKNOWN_PERMISSION, tone: "warning", strip: "bg-warning" },
};
export function RiskBadge({ risk }: { risk: Risk | "unknown" }) {
  if (risk === "standard") return null;
  return (
    <span
      className={
        risk === "money"
          ? "permission-money"
          : risk === "personal-data"
            ? "permission-personal"
            : undefined
      }
    >
      <Badge tone={RISK[risk].tone}>{RISK[risk].label}</Badge>
    </span>
  );
}
export function RiskStrip({
  permissions,
  catalogue,
}: {
  permissions: string[];
  catalogue: CapabilityItem[];
}) {
  const counts = riskCounts(permissions, catalogue);
  const risks = (Object.keys(counts) as (Risk | "unknown")[]).filter(
    (risk) => risk !== "standard" && counts[risk],
  );
  if (!risks.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted">
      {risks.map((risk) => (
        <span key={risk} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={`size-1.5 shrink-0 rounded-control ${RISK[risk].strip}`}
          />
          {risk === "unknown" ? (
            counts[risk] === 1 ? (
              UNKNOWN_PERMISSION
            ) : (
              `${counts[risk]} permissions this console doesn't recognise yet`
            )
          ) : (
            <>
              {counts[risk]} {RISK[risk].label.toLowerCase()}
            </>
          )}
        </span>
      ))}
    </span>
  );
}
export function PermissionDetails({
  permissions,
  description,
}: {
  permissions: string[];
  description?: string;
}) {
  return (
    <div className="mt-1">
      <Details variant="inline" summary="Technical details">
        <Details
          items={[
            ...(description ? [["Catalogue", description] as [string, ReactNode]] : []),
            ...permissions.map((capability): [string, ReactNode] => [
              "Capability",
              <code className="break-all">{capability}</code>,
            ]),
          ]}
        />
      </Details>
    </div>
  );
}
export function PermissionList({
  permissions,
  catalogue,
  reasons = {},
  marker,
  subject = "app",
  extra,
  showTitles = true,
}: {
  permissions: string[];
  catalogue: CapabilityItem[];
  reasons?: Record<string, string>;
  marker?: "+" | "−";
  subject?: "app" | "agent" | "role";
  showTitles?: boolean;
  extra?: (capability: string) => ReactNode;
}) {
  const selected = permissions.map(
    (capability) =>
      catalogue.find((item) => item.capability === capability) ?? {
        capability,
        title: UNKNOWN_PERMISSION,
        group: "Other permissions",
        risk: "unknown" as const,
      },
  );
  const groups = [...new Set(selected.map((item) => item.group))].sort();
  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <div key={group}>
          <h3 className="mb-3 text-sm font-strong text-muted">{group}</h3>
          <ul className="space-y-5">
            {selected
              .filter((item) => item.group === group)
              .map((item) => (
                <li key={item.capability} className="flex gap-3">
                  {marker && (
                    <span
                      aria-hidden="true"
                      className={`text-xl font-strong ${marker === "+" ? "text-positive" : "text-danger"}`}
                    >
                      {marker}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    {showTitles && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-strong">
                          {permissionTitle(item.title)}
                        </span>
                        {item.risk !== "unknown" && (
                          <RiskBadge risk={item.risk} />
                        )}
                      </div>
                    )}
                    <p className="mt-1 text-sm text-muted">
                      {reasons[item.capability]?.trim() ||
                        (subject === "role"
                          ? "Anyone holding this role can use this permission."
                          : `This ${subject} gave no reason for this permission.`)}
                    </p>
                    {extra?.(item.capability)}
                    <PermissionDetails
                      permissions={[item.capability]}
                      description={
                        // The catalogue's whole sentence, only where the short title above left something out.
                        "description" in item && permissionTitle(item.description) !== item.description
                          ? item.description
                          : undefined
                      }
                    />
                  </div>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
/** Shared decision lines; words and glyphs carry every change without relying on colour. */
export function PermissionDiff({
  diff,
  catalogue,
  reasons = {},
  removedReasons = {},
  subject = "app",
  showTitles = true,
}: {
  diff: ClientDiff;
  catalogue: CapabilityItem[];
  reasons?: Record<string, string>;
  removedReasons?: Record<string, string>;
  subject?: "app" | "role" | "agent";
  showTitles?: boolean;
}) {
  const changed =
    diff.added.length +
      diff.removed.length +
      diff.redirectsAdded.length +
      diff.redirectsRemoved.length ||
    diff.keysChanged ||
    diff.nameChanged;
  if (!changed)
    return (
      <EmptyState title="No changes">
        This {subject} already has these permissions.
      </EmptyState>
    );
  return (
    <div className="flex flex-col gap-6">
      {diff.added.length > 0 && (
        <div>
          <h3 className="mb-4 text-sm font-strong text-ink">
            <span className="text-positive">+</span> Added
          </h3>
          <PermissionList
            permissions={diff.added}
            catalogue={catalogue}
            reasons={reasons}
            marker="+"
            subject={subject}
            showTitles={showTitles}
          />
        </div>
      )}
      {diff.removed.length > 0 && (
        <div>
          <h3 className="mb-4 text-sm font-strong text-ink">
            <span className="text-danger">−</span> Removed
          </h3>
          <PermissionList
            permissions={diff.removed}
            catalogue={catalogue}
            reasons={removedReasons}
            marker="−"
            subject={subject}
            showTitles={showTitles}
          />
        </div>
      )}
      {diff.redirectsAdded.map((uri) => (
        <p key={uri} className="break-all">
          <span className="text-positive">+</span> Allow sign-in to return to{" "}
          {uri}
        </p>
      ))}
      {diff.redirectsRemoved.map((uri) => (
        <p key={uri} className="break-all">
          <span className="text-danger">−</span> Stop sign-in returning to {uri}
        </p>
      ))}
      {diff.keysChanged && (
        <p>Change the public keys used to identify this app.</p>
      )}
      {diff.nameChanged && <p>Set the app's name.</p>}
    </div>
  );
}

/** One decision sheet for app/role permission edits and an agent's role change. */
export function PermissionReview({
  name,
  diff,
  catalogue,
  reasons,
  subject = "app",
  title,
  kept,
}: {
  name: string;
  diff: ClientDiff;
  catalogue: CapabilityItem[];
  reasons?: Record<string, string>;
  subject?: "app" | "role" | "agent";
  title?: string;
  kept?: string[];
}) {
  return (
    <div className="flex flex-col gap-6">
      <h2 className="decision-sentence">
        {title ?? permissionConsequence(name, diff, catalogue)}
      </h2>
      {kept && (
        <div>
          <h3 className="mb-4 text-sm font-strong">Keeps these permissions</h3>
          <PermissionList
            permissions={kept}
            catalogue={catalogue}
            subject={subject}
            reasons={reasons}
          />
        </div>
      )}
      {(!kept || diff.added.length > 0 || diff.removed.length > 0) && (
        <PermissionDiff
          diff={diff}
          catalogue={catalogue}
          reasons={reasons}
          removedReasons={reasons}
          subject={subject}
        />
      )}
    </div>
  );
}
